import { useEffect, useState } from 'react';

export const ARROW_PLACEMENT_OPTIONS = {
  left: 'left',
  middle: 'middle',
  right: 'right'
};

export const VERTICAL_ORIENTATION_OPTIONS = {
  popoverClass: {
    top: 'top',
    bottom: 'bottom'
  },
  arrowClass: {
    top: 'trp-popover-arrow-top',
    bottom: 'trp-popover-arrow-bottom'
  }
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined
  });

  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowSize({
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight
      });
    }, 100);

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);
  return windowSize;
};

export const determineArrowPosition = refLocations => {
  const { window, trigger, popover } = refLocations;
  if (window && trigger && popover) {
    const LEFT_SIDE_OF_WINDOW = 0;
    const RIGHT_SIDE_OF_WINDOW = window.width;

    const triggerRefLeftSideLocationX = trigger.x;
    const triggerRefRightSideLocationX =
      triggerRefLeftSideLocationX + trigger.width;

    const popoverLeftSideLocationX = popover.x;
    const popoverRightSideLocationX = popoverLeftSideLocationX + popover.width;

    const leftQuarterBreakpoint = RIGHT_SIDE_OF_WINDOW / 4;
    const rightQuarterBreakpoint = RIGHT_SIDE_OF_WINDOW - leftQuarterBreakpoint;

    if (
      popoverLeftSideLocationX <= LEFT_SIDE_OF_WINDOW ||
      triggerRefLeftSideLocationX <= leftQuarterBreakpoint
    ) {
      return ARROW_PLACEMENT_OPTIONS.left;
    }
    if (
      popoverRightSideLocationX >= RIGHT_SIDE_OF_WINDOW ||
      triggerRefRightSideLocationX >= rightQuarterBreakpoint
    ) {
      return ARROW_PLACEMENT_OPTIONS.right;
    }
    return ARROW_PLACEMENT_OPTIONS.middle;
  }
  return null;
};

export const isRefInTopQuarterOfWindow = refLocations => {
  const { window, trigger, popover } = refLocations;
  if (window && trigger && popover) {
    const windowHeight = window.height;
    const triggerRefLocationY = trigger.y;
    const popoverHeight = popover.height;

    const TOP_OF_WINDOW = 0;
    const topQuarterBreakpoint = windowHeight / 4;
    if (
      triggerRefLocationY - popoverHeight <= TOP_OF_WINDOW ||
      triggerRefLocationY <= topQuarterBreakpoint
    ) {
      return true;
    }
    return false;
  }
  return null;
};

export const verticallyOrientPopover = (references, refInTopQuarter) => {
  const { popover, arrow } = references;
  const { popoverClass, arrowClass } = VERTICAL_ORIENTATION_OPTIONS;
  if (refInTopQuarter) {
    if (popover.classList.contains(popoverClass.top)) {
      popover.classList.remove(popoverClass.top);
      popover.classList.add(popoverClass.bottom);
      arrow.classList.remove(arrowClass.bottom);
      arrow.classList.add(arrowClass.top);
    }
  } else if (popover.classList.contains(popoverClass.bottom)) {
    popover.classList.remove(popoverClass.bottom);
    popover.classList.add(popoverClass.top);
    arrow.classList.add(arrowClass.bottom);
    arrow.classList.remove(arrowClass.top);
  }
};

export const horizontallyOrientPopover = (references, arrowPosition) => {
  const { popover, arrowContainer } = references;
  const classStringBuilder = subString => `trp-popover-arrow-${subString}`;

  Object.values(ARROW_PLACEMENT_OPTIONS).forEach(option => {
    if (option === arrowPosition) {
      popover.classList.add(arrowPosition);
      arrowContainer.classList.add(classStringBuilder(arrowPosition));
    } else {
      popover.classList.remove(option);
      arrowContainer.classList.remove(classStringBuilder(option));
    }
  });
};

export const calcPopoverPlacement = (
  refLocations,
  orientation = {
    popoverBelowTrigger: false,
    horizontalPosition: ARROW_PLACEMENT_OPTIONS.middle
  }
) => {
  const { popoverBelowTrigger, horizontalPosition } = orientation;
  const { trigger, popover } = refLocations;

  if (trigger && popover) {
    const ARROW_MARGIN = 30;

    let popoverLeft;
    const popoverTop = popoverBelowTrigger
      ? trigger.bottom
      : trigger.y - popover.height;

    if (horizontalPosition === ARROW_PLACEMENT_OPTIONS.left) {
      popoverLeft = trigger.x + trigger.width / 2 - ARROW_MARGIN;
    } else if (horizontalPosition === ARROW_PLACEMENT_OPTIONS.right) {
      popoverLeft =
        trigger.x + trigger.width / 2 - (popover.width - ARROW_MARGIN);
    } else {
      popoverLeft = trigger.x + trigger.width / 2 - popover.width / 2;
    }

    return {
      popoverX: `${popoverLeft}px`,
      popoverY: `${popoverTop}px`
    };
  }
  return null;
};

export const affixPopoverToTrigger = (popover, placement) => {
  if (popover && placement) {
    const popoverElement = popover;
    popoverElement.style.left = placement.popoverX;
    popoverElement.style.top = placement.popoverY;
  }
};

export const updatePopoverPlacement = (references, locations) => {
  if (references && locations) {
    const orientation = {
      popoverBelowTrigger: isRefInTopQuarterOfWindow(locations),
      horizontalPosition: determineArrowPosition(locations)
    };

    verticallyOrientPopover(references, orientation.popoverBelowTrigger);
    horizontallyOrientPopover(references, orientation.horizontalPosition);

    const updatedPlacement = calcPopoverPlacement(locations, orientation);
    affixPopoverToTrigger(references.popover, updatedPlacement);
  }
};

export const handleClick = (target, actions, context) => {
  const { toggler, setter } = actions;
  const { popover, trigger } = context;
  const { id } = target;
  const POPOVER_WRAPPER_CLASS = '.trp-popover-wrapper';
  if (id === trigger) {
    toggler();
  } else if (
    id === `${popover}-close-button` ||
    !target.closest(POPOVER_WRAPPER_CLASS)
  ) {
    setter(false);
  }
};
